import {
  EmbeddedMessage,
  MESSAGE_MEDIUMS,
  MessageMedium,
  WhatsApp,
} from 'constants/messageMediums';
import { useCreateCampaignContext } from 'modules/campaignIndex/campaignFolders/contexts/CreateCampaignContext';
import { useCampaignFolderModals } from 'modules/campaignIndex/hooks/useCampaignFolderModals';
import { PropsWithChildren } from 'react';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { Button } from 'ui/button';
import { BUTTON_SIZES } from 'ui/button/constants';
import {
  AddNew16,
  ChevronDown16,
  EmailLine16,
  EmbeddedLine16,
  InAppLine16,
  PushLine16,
  SmsLine16,
  WebPushLine16,
  WhatsAppLine16,
} from 'ui/icons';
import { LeftTooltip } from 'ui/tooltip/Tooltip';
import {
  useEmbeddedMessagingCoreFeature,
  useWhatsAppMessageChannelFeature,
} from 'utils/hooks/featureFlags';
import { StyledActionMenu } from './CreateCampaignMenu.styled';

export const CAMPAIGN_MENU_ID = 'new-campaign-menu';

type CreateCampaignMenuProps = {
  canDraftMessaging: boolean;
  hasWhatsAppAccounts: boolean;
  isLoadingEspAccounts: boolean;
  hasApprovedBusinessTemplates: boolean;
};

export const CreateCampaignMenu = ({
  canDraftMessaging,
  hasWhatsAppAccounts = false,
  isLoadingEspAccounts = false,
  hasApprovedBusinessTemplates,
}: CreateCampaignMenuProps) => {
  const modals = useCampaignFolderModals();
  const { setMessageMedium, setHasApprovedBusinessTemplates } = useCreateCampaignContext();
  const isEmbeddedMessagingCampaignsFeatureOn = useEmbeddedMessagingCoreFeature();
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();

  const options: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <EmailLine16 />
          <span>{MESSAGE_MEDIUMS.EMAIL.label}</span>
        </>
      ),
      id: MessageMedium.Email,
      onClick: () => {
        setMessageMedium(MessageMedium.Email);
        modals.createCampaign.toggle(true);
      },
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <PushLine16 />
          <span>{MESSAGE_MEDIUMS.PUSH.label}</span>
        </>
      ),
      id: MessageMedium.Push,
      onClick: () => {
        setMessageMedium(MessageMedium.Push);
        modals.createCampaign.toggle(true);
      },
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <SmsLine16 />
          <span>{MESSAGE_MEDIUMS.SMS.label}</span>
        </>
      ),
      id: MessageMedium.SMS,
      onClick: () => {
        setMessageMedium(MessageMedium.SMS);
        modals.createCampaign.toggle(true);
      },
    },
    ...(isWhatsAppFFOn
      ? [
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <WhatsAppLine16 />
                <span>WhatsApp</span>
              </>
            ),
            id: WhatsApp,
            isDisabled: !hasWhatsAppAccounts,
            onClick: () => {
              setMessageMedium(WhatsApp);
              setHasApprovedBusinessTemplates(hasApprovedBusinessTemplates);

              modals.createCampaign.toggle(true);
            },
            Wrapper: ({ children }: PropsWithChildren) => (
              <LeftTooltip
                isEnabled={!isLoadingEspAccounts && !hasWhatsAppAccounts}
                tooltipContent="Connect your WhatsApp business account to create a WhatsApp campaign"
              >
                {children}
              </LeftTooltip>
            ),
          },
        ]
      : []),
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <InAppLine16 />
          <span>{MESSAGE_MEDIUMS.IN_APP.label}</span>
        </>
      ),
      id: MessageMedium.InApp,
      onClick: () => {
        setMessageMedium(MessageMedium.InApp);
        modals.createCampaign.toggle(true);
      },
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <WebPushLine16 />
          <span>{MESSAGE_MEDIUMS.WEB_PUSH.label}</span>
        </>
      ),
      id: MessageMedium.WebPush,
      onClick: () => {
        setMessageMedium(MessageMedium.WebPush);
        modals.createCampaign.toggle(true);
      },
    },
    ...(isEmbeddedMessagingCampaignsFeatureOn
      ? [
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <EmbeddedLine16 />
                <span>Embedded</span>
              </>
            ),
            id: EmbeddedMessage,
            onClick: () => {
              setMessageMedium(EmbeddedMessage);
              modals.createCampaign.toggle(true);
            },
          },
        ]
      : []),
  ];

  return (
    <StyledActionMenu
      isDisabled={!canDraftMessaging}
      data-test={CAMPAIGN_MENU_ID}
      options={options}
    >
      <Button isDisabled={!canDraftMessaging} size={BUTTON_SIZES.LARGE}>
        <AddNew16 />
        <span>New campaign</span>
        <ChevronDown16 />
      </Button>
    </StyledActionMenu>
  );
};
